// src/pages/Banners/components/BannerPreviewModal.tsx
import React from "react";
import {Dialog, Transition} from "@headlessui/react";
import {Fragment} from "react";
import {X, Calendar, Clock} from "lucide-react";
import type {BannerResponseDto} from "../../../types/banner";

interface BannerPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  banner?: BannerResponseDto | null;
}

const BannerPreviewModal: React.FC<BannerPreviewModalProps> = ({
  isOpen,
  onClose,
  banner,
}) => {
  if (!banner) return null;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-75" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium text-gray-900 dark:text-white"
                  >
                    Banner Preview
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Banner Image */}
                  <div className="mb-6">
                    <img
                      src={banner.image}
                      alt="Banner"
                      className="w-full max-h-96 object-contain rounded-lg border border-gray-200 dark:border-gray-700"
                    />
                  </div>

                  {/* Banner Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Banner ID
                        </label>
                        <p className="text-sm text-gray-900 dark:text-white">
                          #{banner.id}
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Image URL
                        </label>
                        <p className="text-sm text-gray-900 dark:text-white break-all">
                          {banner.image}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Created
                          </label>
                          <p className="text-sm text-gray-900 dark:text-white">
                            {new Date(banner.createdAt).toLocaleString()}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Last Updated
                          </label>
                          <p className="text-sm text-gray-900 dark:text-white">
                            {new Date(banner.updatedAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default BannerPreviewModal;
